import "./Sprints.css";
import { useMemo, useState } from "react";
import { type Column, Table } from "../../kit/index.js";
import { historyKey, loadHistory, useResource } from "../data.js";
import { dayShort, monthDay, parseDay } from "../model/days.js";
import { raceTime, speedNumber, weightNumber } from "../model/sets.js";
import { seriesOrder, type SprintChart, type SprintSeries, type SprintPoint, type SprintRep, type SprintRow, sprintCharts, sprintReps, sprintRows } from "../model/sprints.js";
import { shownUnit, shownWeight, usePrefs } from "../prefs.js";

const W = 300;
const H = 150;
const PAD = { left: 38, right: 34, top: 16, bottom: 22 };

function loadText(kg: number | null): string {
  return kg == null ? "" : weightNumber(shownWeight(kg, "kg")) + " " + shownUnit("kg");
}

function seriesText(s: { kg: number | null; resisted: boolean }): string {
  return s.kg != null ? loadText(s.kg) : s.resisted ? "load not logged" : "free";
}

const value = (measure: SprintChart["measure"], v: number) => (measure === "speed" ? speedNumber(v) + " m/s" : raceTime(v));

function repText(r: SprintRep): string {
  const parts: string[] = [];
  if (r.speed != null) parts.push(speedNumber(r.speed) + " m/s");
  if (r.time != null) parts.push(raceTime(r.time));
  if (r.suspect != null) parts.push(raceTime(r.suspect) + "?");
  return parts.join(" · ");
}

function otherMeasure(r: SprintRep, measure: SprintChart["measure"]): string {
  if (measure === "speed") return r.time != null ? raceTime(r.time) : "";
  return r.speed != null ? speedNumber(r.speed) + " m/s" : "";
}

/** DESIGN §40: each distance's best rep a day, by load, over the athlete's sprint days. */
export function Sprints({ athleteID }: { athleteID: string }) {
  usePrefs();
  const history = useResource(historyKey(athleteID), () => loadHistory(athleteID));
  const workouts = history.value?.workouts;
  const reps = useMemo(() => sprintReps(workouts ?? []), [workouts]);
  if (reps.length === 0) return null;
  const charts = sprintCharts(reps);
  const rows = sprintRows(reps);
  const dates = reps.map((r) => r.date).sort();
  const span = { from: dates[0]!, to: dates[dates.length - 1]! };
  const loads = new Map<SprintSeries, { kg: number | null; resisted: boolean }>();
  for (const c of charts) for (const s of c.series) loads.set(s.key, s);
  const order = [...loads.keys()].sort(seriesOrder);

  const columns: Column<SprintRow>[] = [
    {
      key: "date",
      label: "Date",
      render: (r) => (
        <>
          <span className="num">{dayShort(r.date)}</span>
          <span className="k-phone-only k-sub num">{[r.meters + " m", r.kg != null ? loadText(r.kg) : "", r.name].filter(Boolean).join(" · ")}</span>
        </>
      ),
    },
    { key: "run", label: "Run", wrap: true, className: "k-hide-phone", render: (r) => r.name },
    { key: "distance", label: "Distance", align: "end", className: "k-hide-phone", render: (r) => <span className="num">{r.meters} m</span> },
    { key: "load", label: "Load", className: "k-hide-phone", render: (r) => (r.kg != null ? <span className="num">{loadText(r.kg)}</span> : <span className="k-muted">—</span>) },
    {
      key: "reps",
      label: "Reps",
      wrap: true,
      render: (r) => (
        <span className="spr-reps num">
          {r.reps.map((rep, i) => (
            <span key={i} className={i === r.best ? "is-best" : undefined} title={rep.suspect != null ? "May be a speed typed as a time; left out of the charts" : undefined}>
              {repText(rep)}
            </span>
          ))}
        </span>
      ),
    },
  ];

  return (
    <section className="ath-section" id="ath-sprints" aria-labelledby="ath-sprints-h">
      <div className="ath-sec-head">
        <h2 id="ath-sprints-h">Sprints</h2>
        <span className="ath-sec-note">By distance and load</span>
      </div>
      <div className="ath-card">
        <div className="spr-charts">
          {charts.map((c) => (
            <SprintChartView key={c.meters} chart={c} span={span} order={order} />
          ))}
        </div>
        <div className="ath-chart-keys spr-keys" aria-hidden="true">
          {order.map((k) => (
            <span key={k} className="ath-key">
              <svg width="12" height="12" viewBox="0 0 12 12" className={"spr-swatch " + tone(k, order)}>
                <circle cx="6" cy="6" r={k === "free" ? 3 : 4.5} className={"spr-dot" + (k === "resisted" ? " is-hollow" : "")} />
              </svg>
              {seriesText(loads.get(k)!)}
            </span>
          ))}
        </div>
        <h3 className="ath-card-sub">Every rep</h3>
        <div className="ath-card-bleed">
          <Table rows={rows} columns={columns} rowKey={(r) => [r.date, r.name, r.meters, r.kg ?? ""].join("|")} label="Every sprint rep" />
        </div>
      </div>
    </section>
  );
}

function tone(key: string, order: string[]): string {
  if (key === "free") return "is-free";
  if (key === "resisted") return "is-unloaded";
  const loaded = order.filter((k) => k.startsWith("kg:"));
  return "is-load-" + (loaded.indexOf(key) % 4);
}

function SprintChartView({ chart, span, order }: { chart: SprintChart; span: { from: string; to: string }; order: string[] }) {
  const [active, setActive] = useState<string | null>(null);
  const all = chart.series.flatMap((s) => s.points.map((p) => p.value));
  let lo = Math.min(...all);
  let hi = Math.max(...all);
  const least = chart.measure === "speed" ? 0.4 : 0.1;
  if (hi - lo < least) {
    const mid = (hi + lo) / 2;
    lo = mid - least / 2;
    hi = mid + least / 2;
  }
  const pad = (hi - lo) * 0.15;
  lo -= pad;
  hi += pad;
  const t0 = parseDay(span.from).getTime();
  const t1 = parseDay(span.to).getTime();
  const x = (d: string) => (t1 === t0 ? (PAD.left + W - PAD.right) / 2 : PAD.left + ((parseDay(d).getTime() - t0) / (t1 - t0)) * (W - PAD.left - PAD.right));
  const up = chart.measure === "speed";
  const y = (v: number) => {
    const f = (v - lo) / (hi - lo);
    return PAD.top + (up ? 1 - f : f) * (H - PAD.top - PAD.bottom);
  };
  const ticks = [lo + pad, hi - pad];
  const title = chart.meters + " m · " + (up ? "top speed" : "time");
  const id = (s: string, p: SprintPoint) => s + "|" + p.date;
  // Each load's label sits in the right margin at its latest value; labels that would overlap are pushed apart.
  const labels = new Map<string, number>();
  let prev = -Infinity;
  for (const s of chart.series
    .filter((s) => s.kg != null)
    .map((s) => ({ key: s.key, y: y(s.points[s.points.length - 1]!.value) + 4 }))
    .sort((a, b) => a.y - b.y)) {
    prev = Math.max(s.y, prev + 12);
    labels.set(s.key, prev);
  }
  let tip: { left: number; top: number; point: SprintPoint; series: (typeof chart.series)[number] } | null = null;
  for (const s of chart.series) for (const p of s.points) if (active === id(s.key, p)) tip = { left: (x(p.date) / W) * 100, top: (y(p.value) / H) * 100, point: p, series: s };

  return (
    <figure className="spr-chart">
      <figcaption className="spr-title">
        <b className="num">{chart.meters} m</b> {up ? "Top speed" : "Time"}
      </figcaption>
      <div className="ath-chart-box spr-box">
        <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={title}>
          {ticks.map((v, i) => (
            <g key={i}>
              <line className="grid-line" x1={PAD.left} x2={W - PAD.right} y1={y(v)} y2={y(v)} />
              <text className="axis" x={PAD.left - 6} y={y(v) + 4} textAnchor="end">
                {up ? speedNumber(v) : raceTime(v, false)}
              </text>
            </g>
          ))}
          <text className="axis" x={PAD.left} y={H - 4} textAnchor="start">
            {monthDay(span.from)}
          </text>
          {span.to !== span.from ? (
            <text className="axis" x={W - PAD.right} y={H - 4} textAnchor="end">
              {monthDay(span.to)}
            </text>
          ) : null}
          {chart.series.map((s) => {
            const cls = tone(s.key, order);
            return (
              <g key={s.key} className={cls}>
                {s.points.length > 1 ? <polyline className="spr-line" points={s.points.map((p) => x(p.date) + "," + y(p.value)).join(" ")} /> : null}
                {s.points.map((p) => (
                  <circle
                    key={p.date}
                    className={"spr-dot" + (s.key === "resisted" ? " is-hollow" : "") + (active === id(s.key, p) ? " is-active" : "")}
                    cx={x(p.date)}
                    cy={y(p.value)}
                    r={s.key === "free" ? 3.5 : 5}
                    tabIndex={0}
                    aria-label={`${dayShort(p.date)}, ${seriesText(s)}, ${value(chart.measure, p.value)}`}
                    onMouseEnter={() => setActive(id(s.key, p))}
                    onMouseLeave={() => setActive(null)}
                    onFocus={() => setActive(id(s.key, p))}
                    onBlur={() => setActive(null)}
                  />
                ))}
                {s.kg != null ? (
                  <text className="spr-label" x={W - PAD.right + 8} y={labels.get(s.key)}>
                    {loadText(s.kg)}
                  </text>
                ) : null}
              </g>
            );
          })}
        </svg>
        {tip ? (
          <div className="ath-tip" style={{ left: tip.left + "%", top: tip.top + "%" }}>
            <b className="num">{value(chart.measure, tip.point.value)}</b>
            <span className="num">{[otherMeasure(tip.point.rep, chart.measure), seriesText(tip.series)].filter(Boolean).join(" · ")}</span>
            <span>
              {tip.point.rep.name} · {dayShort(tip.point.date)}
            </span>
          </div>
        ) : null}
      </div>
      <table className="sr">
        <caption>{title}</caption>
        <thead>
          <tr>
            <th scope="col">Date</th>
            <th scope="col">Load</th>
            <th scope="col">{up ? "Top speed" : "Time"}</th>
          </tr>
        </thead>
        <tbody>
          {chart.series.flatMap((s) =>
            s.points.map((p) => (
              <tr key={s.key + p.date}>
                <td>{dayShort(p.date)}</td>
                <td>{seriesText(s)}</td>
                <td>{value(chart.measure, p.value)}</td>
              </tr>
            )),
          )}
        </tbody>
      </table>
    </figure>
  );
}
